import React, { useState, useEffect, useCallback, useMemo, Suspense } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  Box,
  IconButton,
  Tooltip,
  LinearProgress,
  Fade,
  Paper,
  Chip,
  Stack,
  FormHelperText
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { 
  InfoOutlined, 
  LightbulbOutlined,
  ArrowBack,
  ArrowForward
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next'; // Importación añadida para traducción

// Definimos una constante para el color azul que queremos usar en ambos modos
const blue = '#007BFE';

// Definición de la paleta de colores utilizando la misma variable 'blue'
const colors = {
  light: {
    label: {
      primary: '#000000'
    },
    system: {
      grey: '#8E8E93',
      grey3: '#C7C7CC',
      white: '#FFFFFF',
      black: '#000000',
      green: '#31C859' // Éxito
    },
    bg: {
      primaryElevated: '#FFFFFF'
    },
    fill: {
      tertiary: '#767680'
    },
    ui: {
      accent: blue,
      backgroundBlur: '#FFFFFF',
      base: '#F2F2F7' // Tonalidad clara para las tarjetas
    },
    grouped: {
      bgPrimaryBase: '#FFFFFF'
    }
  },
  dark: {
    label: {
      primary: '#FFFFFF'
    },
    system: {
      grey: '#8E8E93',
      grey3: '#464649',
      white: '#FFFFFF',
      black: '#000000',
      green: '#2DD257' // Éxito en modo oscuro
    },
    bg: {
      primaryElevated: '#000000'
    },
    fill: {
      tertiary: '#767680'
    },
    ui: {
      // Usamos el mismo color azul definido en la constante
      accent: blue,
      backgroundBlur: '#282828',
      base: '#272729'
    },
    grouped: {
      bgPrimaryBase: '#000000'
    }
  }
};

// Lista completa de monedas
const currencies = [
  { code: 'USD', name: 'US Dollar' },
  { code: 'EUR', name: 'Euro' },
  { code: 'GBP', name: 'British Pound Sterling' },
  { code: 'JPY', name: 'Japanese Yen' },
  { code: 'AUD', name: 'Australian Dollar' },
  { code: 'CAD', name: 'Canadian Dollar' },
  { code: 'CHF', name: 'Swiss Franc' },
  { code: 'CNY', name: 'Chinese Yuan' },
  { code: 'HKD', name: 'Hong Kong Dollar' },
  { code: 'NZD', name: 'New Zealand Dollar' },
  { code: 'SEK', name: 'Swedish Krona' },
  { code: 'KRW', name: 'South Korean Won' },
  { code: 'SGD', name: 'Singapore Dollar' },
  { code: 'NOK', name: 'Norwegian Krone' },
  { code: 'MXN', name: 'Mexican Peso' },
  { code: 'INR', name: 'Indian Rupee' },
  { code: 'RUB', name: 'Russian Ruble' },
  { code: 'ZAR', name: 'South African Rand' },
  { code: 'TRY', name: 'Turkish Lira' },
  { code: 'BRL', name: 'Brazilian Real' },
  { code: 'TWD', name: 'New Taiwan Dollar' },
  { code: 'DKK', name: 'Danish Krone' },
  { code: 'PLN', name: 'Polish Złoty' },
  { code: 'THB', name: 'Thai Baht' },
  { code: 'IDR', name: 'Indonesian Rupiah' },
  { code: 'HUF', name: 'Hungarian Forint' },
  { code: 'CZK', name: 'Czech Koruna' },
  { code: 'ILS', name: 'Israeli New Shekel' },
  { code: 'CLP', name: 'Chilean Peso' },
  { code: 'PHP', name: 'Philippine Peso' },
  { code: 'AED', name: 'United Arab Emirates Dirham' },
  { code: 'COP', name: 'Colombian Peso' },
  { code: 'SAR', name: 'Saudi Riyal' },
  { code: 'MYR', name: 'Malaysian Ringgit' },
  { code: 'RON', name: 'Romanian Leu' },
  { code: 'ARS', name: 'Argentine Peso' },
  { code: 'BGN', name: 'Bulgarian Lev' },
  { code: 'PEN', name: 'Peruvian Sol' },
  { code: 'VND', name: 'Vietnamese Dong' },
  { code: 'UAH', name: 'Ukrainian Hryvnia' },
  { code: 'NGN', name: 'Nigerian Naira' },
  { code: 'PKR', name: 'Pakistani Rupee' },
  { code: 'EGP', name: 'Egyptian Pound' },
  { code: 'BDT', name: 'Bangladeshi Taka' },
  { code: 'BTC', name: 'Bitcoin' },
  { code: 'ETH', name: 'Ethereum' },
  { code: 'OTHER', name: 'Other Currency' }
];

// Eliminamos la sombra del diálogo (el componente Paper interno)
const StyledDialog = styled(Dialog)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    // Z-index bajo para estar debajo del TopBar (que tiene 1100)
    zIndex: '1000 !important',
    pointerEvents: 'none', // El Dialog root NO debe bloquear clicks
    // El contenedor del Dialog NO debe bloquear clicks - deben pasar a través
    '& .MuiDialog-container': {
      pointerEvents: 'none !important',
      // En móvil, alinear arriba y dejar espacio para el TopBar
      [theme.breakpoints.down('sm')]: {
        alignItems: 'flex-start',
        paddingTop: '60px' // Espacio para el TopBar en móvil (44px de altura + margen)
      }
    },
    '& .MuiDialog-paper': {
      // Solo el Paper (el wizard en sí) debe capturar clicks
      pointerEvents: 'auto',
      borderRadius: '28px',
      padding: theme.spacing(2),
      overflow: 'hidden',
      boxShadow: 'none',
      backgroundColor: isWallpaperMode
        ? 'rgba(128, 128, 128, 0.3)'
        : (theme.palette.mode === 'dark' ? theme.palette.background.paper : '#FFFFFF'),
      backdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
      WebkitBackdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
      border: isWallpaperMode ? '1px solid rgba(128, 128, 128, 0.3)' : 'none'
    }
    // No necesitamos estilos para el backdrop porque lo hemos desactivado con hideBackdrop={true}
  };
});

const StepContent = styled(DialogContent)(({ theme }) => ({
  position: 'relative',
  padding: theme.spacing(3),
  transition: 'all 0.3s ease-in-out',
  '&.slide-enter': {
    transform: 'translateX(100%)',
    opacity: 0
  },
  '&.slide-enter-active': {
    transform: 'translateX(0)',
    opacity: 1
  },
  '&.slide-exit': {
    transform: 'translateX(-100%)',
    opacity: 0
  }
}));

const StepIndicator = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  marginBottom: theme.spacing(2),
  gap: theme.spacing(1)
}));

const StepDot = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'active' && prop !== 'completed'
})(({ theme, active, completed }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';
  return {
    width: 10,
    height: 10,
    borderRadius: '50%',
    backgroundColor: active
      ? (isWallpaperMode ? '#FFFFFF' : theme.palette.primary.main)
      : completed
        ? (isWallpaperMode ? 'rgba(255, 255, 255, 0.6)' : theme.palette.primary.light)
        : (isWallpaperMode ? 'rgba(255, 255, 255, 0.3)' : theme.palette.grey[300]),
    transition: 'all 0.3s ease-in-out',
    transform: active ? 'scale(1.2)' : 'scale(1)',
    cursor: 'pointer',
    '&:hover': {
      transform: 'scale(1.1)'
    }
  };
});

// Componente Tooltip sin sombra
const NoShadowTooltip = styled(Tooltip)(() => ({
  '& .MuiTooltip-tooltip': {
    boxShadow: 'none'
  },
  '& .MuiTooltip-arrow': {
    boxShadow: 'none'
  }
}));

// Componente de resumen modificado
function SummaryContent({ steps, profileData }) {
  const { t } = useTranslation(); // Hook para traducciones
  
  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        {t('agtUps.profileSetup.summary')}
      </Typography>
      <Stack spacing={2}>
        {steps.map((step, index) => {
          const fieldParts = step.field.split('.');
          const value = fieldParts.length > 1
            ? (profileData[fieldParts[0]] ? profileData[fieldParts[0]][fieldParts[1]] : '')
            : profileData[step.field];
            
          // Formato especial para moneda
          let displayValue = value || t('agtUps.profileSetup.notProvided');
          if (step.field === 'currency' && value) {
            const currencyObj = currencies.find(c => c.code === value);
            if (currencyObj) {
              displayValue = `${currencyObj.name} (${currencyObj.code})`;
            }
          }
          
          return (
            <Paper
              key={index}
              sx={(theme) => {
                const isWallpaperMode = theme.palette.actualMode === 'wallpaper';
                return {
                  p: 2,
                  borderRadius: '16px',
                  boxShadow: 'none',
                  backgroundColor: isWallpaperMode
                    ? 'rgba(128, 128, 128, 0.3)'
                    : (theme.palette.mode === 'light'
                      ? colors.light.ui.base
                      : theme.palette.background.paper),
                  backdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
                  WebkitBackdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
                  border: isWallpaperMode ? '1px solid rgba(128, 128, 128, 0.3)' : 'none'
                };
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography
                  variant="subtitle1"
                  sx={(theme) => ({
                    color: theme.palette.actualMode === 'wallpaper'
                      ? '#FFFFFF'
                      : theme.palette.primary.main
                  })}
                >
                  {step.title}
                </Typography>
                {step.required && (
                  <Chip
                    size="small"
                    label={t('agtUps.profileSetup.required')}
                    sx={(theme) => {
                      const isWallpaperMode = theme.palette.actualMode === 'wallpaper';
                      return {
                        backgroundColor: isWallpaperMode ? 'rgba(255, 255, 255, 0.2)' : undefined,
                        color: isWallpaperMode ? '#FFFFFF' : undefined,
                        borderColor: isWallpaperMode ? 'rgba(255, 255, 255, 0.5)' : undefined
                      };
                    }}
                    variant="outlined"
                  />
                )}
              </Box>
              <Typography
                variant="body2"
                sx={(theme) => ({
                  mt: 1,
                  color: theme.palette.actualMode === 'wallpaper'
                    ? 'rgba(255, 255, 255, 0.85)'
                    : 'text.secondary'
                })}
              >
                {displayValue}
              </Typography>
            </Paper>
          );
        })}
      </Stack>
    </Box>
  );
}

const ProfileSetupWizard = ({ open, onClose, profileData, onChange, onSubmit, onSaveDraft }) => {
  const { t } = useTranslation(); // Hook para traducciones
  const [activeStep, setActiveStep] = useState(0);
  const [transitioning, setTransitioning] = useState(false);
  const [locationTipShown, setLocationTipShown] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const steps = useMemo(() => [
    {
      title: t('agtUps.profileSetup.companyName.title'),
      description: t('agtUps.profileSetup.companyName.description'),
      required: true,
      field: 'companyName',
      minLength: 2,
      component: (
        <TextField
          fullWidth
          label={t('agtUps.profileSetup.companyName.title')}
          name="companyName"
          value={profileData.companyName}
          onChange={onChange}
          sx={(theme) => {
            const isWallpaperMode = theme.palette.actualMode === 'wallpaper';
            return {
              mt: 2,
              '& .MuiOutlinedInput-root': {
                borderRadius: '28px',
                backgroundColor: isWallpaperMode ? 'rgba(128, 128, 128, 0.3)' : undefined,
                backdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
                WebkitBackdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
                '& fieldset': {
                  borderColor: isWallpaperMode ? 'rgba(255, 255, 255, 0.3)' : undefined
                },
                '&:hover fieldset': {
                  borderColor: isWallpaperMode ? 'rgba(255, 255, 255, 0.5)' : undefined
                },
                '&.Mui-focused fieldset': {
                  borderColor: isWallpaperMode ? '#FFFFFF' : undefined
                }
              },
              '& .MuiInputBase-input': {
                color: isWallpaperMode ? '#FFFFFF' : undefined
              },
              '& .MuiInputLabel-root': {
                color: isWallpaperMode ? 'rgba(255, 255, 255, 0.7)' : undefined,
                '&.Mui-focused': {
                  color: isWallpaperMode ? '#FFFFFF' : undefined
                }
              }
            };
          }}
        />
      )
    },
    {
      title: t('agtUps.profileSetup.industry.title'),
      description: t('agtUps.profileSetup.industry.description'),
      required: true,
      field: 'industry',
      minLength: 1,
      component: (
        <FormControl
          fullWidth
          sx={(theme) => {
            const isWallpaperMode = theme.palette.actualMode === 'wallpaper';
            return {
              mt: 2,
              '& .MuiOutlinedInput-root': {
                borderRadius: '28px',
                backgroundColor: isWallpaperMode ? 'rgba(128, 128, 128, 0.3)' : undefined,
                backdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
                WebkitBackdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
                '& fieldset': {
                  borderColor: isWallpaperMode ? 'rgba(255, 255, 255, 0.3)' : undefined
                },
                '&:hover fieldset': {
                  borderColor: isWallpaperMode ? 'rgba(255, 255, 255, 0.5)' : undefined
                },
                '&.Mui-focused fieldset': {
                  borderColor: isWallpaperMode ? '#FFFFFF' : undefined
                }
              },
              '& .MuiInputBase-input': {
                color: isWallpaperMode ? '#FFFFFF' : undefined
              },
              '& .MuiInputLabel-root': {
                color: isWallpaperMode ? 'rgba(255, 255, 255, 0.7)' : undefined,
                '&.Mui-focused': {
                  color: isWallpaperMode ? '#FFFFFF' : undefined
                }
              },
              '& .MuiSvgIcon-root': {
                color: isWallpaperMode ? 'rgba(255, 255, 255, 0.7)' : undefined
              }
            };
          }}
        >
          <InputLabel>{t('agtUps.profileSetup.industry.title')}</InputLabel>
          <Select
            name="industry"
            value={profileData.industry}
            onChange={onChange}
            label={t('agtUps.profileSetup.industry.title')}
          >
            <MenuItem value="technology">{t('agtUps.profileSetup.industry.options.technology')}</MenuItem>
            <MenuItem value="retail">{t('agtUps.profileSetup.industry.options.retail')}</MenuItem>
            <MenuItem value="manufacturing">{t('agtUps.profileSetup.industry.options.manufacturing')}</MenuItem>
            <MenuItem value="services">{t('agtUps.profileSetup.industry.options.services')}</MenuItem>
            <MenuItem value="other">{t('agtUps.profileSetup.industry.options.other')}</MenuItem>
          </Select>
        </FormControl>
      )
    },
    {
      title: t('agtUps.profileSetup.businessStage.title'),
      description: t('agtUps.profileSetup.businessStage.description'),
      required: true,
      field: 'business.stage',
      minLength: 1,
      component: (
        <Box sx={{ mt: 2, display: 'flex', alignItems: 'start', gap: 1 }}>
          <FormControl
            fullWidth
            sx={(theme) => {
              const isWallpaperMode = theme.palette.actualMode === 'wallpaper';
              return {
                flex: 1,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '28px',
                  backgroundColor: isWallpaperMode ? 'rgba(128, 128, 128, 0.3)' : undefined,
                  backdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
                  WebkitBackdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
                  '& fieldset': {
                    borderColor: isWallpaperMode ? 'rgba(255, 255, 255, 0.3)' : undefined
                  },
                  '&:hover fieldset': {
                    borderColor: isWallpaperMode ? 'rgba(255, 255, 255, 0.5)' : undefined
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: isWallpaperMode ? '#FFFFFF' : undefined
                  }
                },
                '& .MuiInputBase-input': {
                  color: isWallpaperMode ? '#FFFFFF' : undefined
                },
                '& .MuiInputLabel-root': {
                  color: isWallpaperMode ? 'rgba(255, 255, 255, 0.7)' : undefined,
                  '&.Mui-focused': {
                    color: isWallpaperMode ? '#FFFFFF' : undefined
                  }
                },
                '& .MuiSvgIcon-root': {
                  color: isWallpaperMode ? 'rgba(255, 255, 255, 0.7)' : undefined
                }
              };
            }}
          >
            <InputLabel>{t('agtUps.profileSetup.businessStage.title')}</InputLabel>
            <Select
              name="business.stage"
              value={profileData.business?.stage}
              onChange={onChange}
              label={t('agtUps.profileSetup.businessStage.title')}
            >
              <MenuItem value="startup">{t('agtUps.profileSetup.businessStage.options.startup')}</MenuItem>
              <MenuItem value="growth">{t('agtUps.profileSetup.businessStage.options.growth')}</MenuItem>
              <MenuItem value="established">{t('agtUps.profileSetup.businessStage.options.established')}</MenuItem>
              <MenuItem value="expanding">{t('agtUps.profileSetup.businessStage.options.expanding')}</MenuItem>
              <MenuItem value="unknown">{t('agtUps.profileSetup.businessStage.options.unknown')}</MenuItem>
            </Select>
          </FormControl>
          <NoShadowTooltip
            arrow
            placement="right"
            title={
              <Box>
                <Typography variant="body2">
                  <strong>{t('agtUps.profileSetup.businessStage.options.startup')}:</strong> {t('agtUps.profileSetup.businessStage.descriptions.startup')}
                </Typography>
                <Typography variant="body2">
                  <strong>{t('agtUps.profileSetup.businessStage.options.growth')}:</strong> {t('agtUps.profileSetup.businessStage.descriptions.growth')}
                </Typography>
                <Typography variant="body2">
                  <strong>{t('agtUps.profileSetup.businessStage.options.established')}:</strong> {t('agtUps.profileSetup.businessStage.descriptions.established')}
                </Typography>
                <Typography variant="body2">
                  <strong>{t('agtUps.profileSetup.businessStage.options.expanding')}:</strong> {t('agtUps.profileSetup.businessStage.descriptions.expanding')}
                </Typography>
                <Typography variant="body2">
                  <strong>{t('agtUps.profileSetup.businessStage.options.unknown')}:</strong> {t('agtUps.profileSetup.businessStage.descriptions.unknown')}
                </Typography>
              </Box>
            }
          >
            <IconButton
              size="small"
              sx={(theme) => {
                const isWallpaperMode = theme.palette.actualMode === 'wallpaper';
                return {
                  color: isWallpaperMode ? '#FFFFFF' : '#0385FF',
                  backgroundColor: isWallpaperMode ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                  '&:hover': {
                    backgroundColor: isWallpaperMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(3, 133, 255, 0.08)'
                  }
                };
              }}
            >
              <InfoOutlined fontSize="small" />
            </IconButton>
          </NoShadowTooltip>
        </Box>
      )
    },
    {
      title: t('agtUps.profileSetup.currency.title'),
      description: t('agtUps.profileSetup.currency.description'),
      required: true,
      field: 'currency',
      minLength: 1,
      component: (
        <FormControl
          fullWidth
          sx={(theme) => {
            const isWallpaperMode = theme.palette.actualMode === 'wallpaper';
            return {
              mt: 2,
              '& .MuiOutlinedInput-root': {
                borderRadius: '28px',
                backgroundColor: isWallpaperMode ? 'rgba(128, 128, 128, 0.3)' : undefined,
                backdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
                WebkitBackdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
                '& fieldset': {
                  borderColor: isWallpaperMode ? 'rgba(255, 255, 255, 0.3)' : undefined
                },
                '&:hover fieldset': {
                  borderColor: isWallpaperMode ? 'rgba(255, 255, 255, 0.5)' : undefined
                },
                '&.Mui-focused fieldset': {
                  borderColor: isWallpaperMode ? '#FFFFFF' : undefined
                }
              },
              '& .MuiInputBase-input': {
                color: isWallpaperMode ? '#FFFFFF' : undefined
              },
              '& .MuiInputLabel-root': {
                color: isWallpaperMode ? 'rgba(255, 255, 255, 0.7)' : undefined,
                '&.Mui-focused': {
                  color: isWallpaperMode ? '#FFFFFF' : undefined
                }
              },
              '& .MuiFormHelperText-root': {
                color: isWallpaperMode ? 'rgba(255, 255, 255, 0.7)' : undefined
              },
              '& .MuiSvgIcon-root': {
                color: isWallpaperMode ? 'rgba(255, 255, 255, 0.7)' : undefined
              }
            };
          }}
        >
          <InputLabel>{t('agtUps.profileSetup.currency.title')}</InputLabel>
          <Select
            name="currency"
            value={profileData.currency || ''}
            onChange={onChange}
            label={t('agtUps.profileSetup.currency.title')}
            MenuProps={{
              PaperProps: {
                style: {
                  maxHeight: 300
                }
              }
            }}
          >
            {currencies.map((currency) => (
              <MenuItem key={currency.code} value={currency.code}>
                {currency.name} ({currency.code})
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>
            {t('agtUps.profileSetup.currency.helpText')}
          </FormHelperText>
        </FormControl>
      )
    },
    {
      title: t('agtUps.profileSetup.location.title'),
      description: t('agtUps.profileSetup.location.description'),
      required: true,
      field: 'location',
      minLength: 3,
      component: (
        <>
          <TextField
            fullWidth
            label={t('agtUps.profileSetup.location.title')}
            name="location"
            value={profileData.location}
            onChange={onChange}
            onBlur={(e) => {
              if (e.target.value.trim() !== '') {
                setLocationTipShown(true);
              }
            }}
            onFocus={() => setLocationTipShown(false)}
            sx={(theme) => {
              const isWallpaperMode = theme.palette.actualMode === 'wallpaper';
              return {
                mt: 2,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '28px',
                  backgroundColor: isWallpaperMode ? 'rgba(128, 128, 128, 0.3)' : undefined,
                  backdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
                  WebkitBackdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
                  '& fieldset': {
                    borderColor: isWallpaperMode ? 'rgba(255, 255, 255, 0.3)' : undefined
                  },
                  '&:hover fieldset': {
                    borderColor: isWallpaperMode ? 'rgba(255, 255, 255, 0.5)' : undefined
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: isWallpaperMode ? '#FFFFFF' : undefined
                  }
                },
                '& .MuiInputBase-input': {
                  color: isWallpaperMode ? '#FFFFFF' : undefined
                },
                '& .MuiInputLabel-root': {
                  color: isWallpaperMode ? 'rgba(255, 255, 255, 0.7)' : undefined,
                  '&.Mui-focused': {
                    color: isWallpaperMode ? '#FFFFFF' : undefined
                  }
                }
              };
            }}
          />
          {locationTipShown && (
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
              <LightbulbOutlined
                fontSize="small"
                sx={(theme) => ({
                  mr: 1,
                  color: theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : '#0385FF'
                })}
              />
              <Typography
                variant="body2"
                sx={(theme) => ({
                  color: theme.palette.actualMode === 'wallpaper'
                    ? 'rgba(255, 255, 255, 0.85)'
                    : 'text.secondary'
                })}
              >
                {t('agtUps.profileSetup.location.tip')}
              </Typography>
            </Box>
          )}
        </>
      )
    },
    {
      title: t('agtUps.profileSetup.companyDescription.title'),
      description: t('agtUps.profileSetup.companyDescription.description'),
      required: false,
      field: 'companyDescription',
      component: (
        <TextField
          fullWidth
          label={t('agtUps.profileSetup.companyDescription.label')}
          name="companyDescription"
          value={profileData.companyDescription}
          onChange={onChange}
          multiline
          rows={4}
          sx={(theme) => {
            const isWallpaperMode = theme.palette.actualMode === 'wallpaper';
            return {
              mt: 2,
              '& .MuiOutlinedInput-root': {
                borderRadius: '28px',
                backgroundColor: isWallpaperMode ? 'rgba(128, 128, 128, 0.3)' : undefined,
                backdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
                WebkitBackdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
                '& fieldset': {
                  borderColor: isWallpaperMode ? 'rgba(255, 255, 255, 0.3)' : undefined
                },
                '&:hover fieldset': {
                  borderColor: isWallpaperMode ? 'rgba(255, 255, 255, 0.5)' : undefined
                },
                '&.Mui-focused fieldset': {
                  borderColor: isWallpaperMode ? '#FFFFFF' : undefined
                }
              },
              '& .MuiInputBase-input': {
                color: isWallpaperMode ? '#FFFFFF' : undefined
              },
              '& .MuiInputLabel-root': {
                color: isWallpaperMode ? 'rgba(255, 255, 255, 0.7)' : undefined,
                '&.Mui-focused': {
                  color: isWallpaperMode ? '#FFFFFF' : undefined
                }
              }
            };
          }}
        />
      )
    }
  ], [profileData, onChange, locationTipShown, t]);

  const validateStep = useCallback((stepIndex, value) => {
    const step = steps[stepIndex];
    if (!step.required) return true;
    
    if (!value || value.trim().length < (step.minLength || 1)) {
      return false;
    }
    return true;
  }, [steps]);

  const isStepValid = useCallback((stepIndex) => {
    const step = steps[stepIndex];
    if (!step.required) return true;

    const fieldParts = step.field.split('.');
    const value = fieldParts.length > 1
      ? (profileData[fieldParts[0]] ? profileData[fieldParts[0]][fieldParts[1]] : '')
      : profileData[step.field];

    return validateStep(stepIndex, value);
  }, [profileData, steps, validateStep]);

  const handleStepTransition = useCallback((nextStep) => {
    setTransitioning(true);
    const timeout = setTimeout(() => {
      setActiveStep(nextStep);
      setTransitioning(false);
    }, 300);
    return () => clearTimeout(timeout);
  }, []);

  const handleNext = useCallback(async () => {
    if (!isStepValid(activeStep)) return;
    
    if (activeStep === steps.length - 1) {
      if (!showSummary) {
        setShowSummary(true);
        return;
      }
      setIsSubmitting(true);
      try {
        await onSubmit();
      } finally {
        setIsSubmitting(false);
      }
      return;
    }
    handleStepTransition(activeStep + 1);
  }, [activeStep, steps.length, showSummary, onSubmit, isStepValid, handleStepTransition]);

  const handleBack = useCallback(() => {
    if (showSummary) {
      setShowSummary(false);
      return;
    }
    handleStepTransition(activeStep - 1);
  }, [activeStep, showSummary, handleStepTransition]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleNext();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        handleBack();
      }
    };
    if (open) {
      window.addEventListener('keydown', handleKeyDown);
    }
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, handleNext, handleBack]);

  const progressText = t('agtUps.profileSetup.navigation.step', { current: activeStep + 1, total: steps.length });
  const summaryContent = useMemo(() => 
    <SummaryContent steps={steps} profileData={profileData} />, 
    [steps, profileData]
  );

  return (
    <StyledDialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      TransitionComponent={Fade}
      transitionDuration={300}
      hideBackdrop={true}
      disableEnforceFocus
      disableAutoFocus
      disableRestoreFocus
      sx={{
        zIndex: 1000,
        pointerEvents: 'none'
      }}
      slotProps={{
        backdrop: {
          sx: {
            pointerEvents: 'none'
          }
        }
      }}
    >
      <Box sx={{ width: '100%', mb: 1 }}>
        <LinearProgress
          variant="determinate"
          value={((activeStep + (showSummary ? 1 : 0)) / steps.length) * 100}
          sx={(theme) => {
            const isWallpaperMode = theme.palette.actualMode === 'wallpaper';
            return {
              height: 6,
              borderRadius: 3,
              backgroundColor: isWallpaperMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0,123,254,0.12)',
              '& .MuiLinearProgress-bar': {
                backgroundColor: isWallpaperMode ? '#FFFFFF' : blue,
                transition: 'transform 0.5s ease-in-out'
              }
            };
          }}
        />
        <Typography
          variant="caption"
          align="center"
          sx={(theme) => ({
            display: 'block',
            mt: 0.5,
            color: theme.palette.actualMode === 'wallpaper'
              ? 'rgba(255, 255, 255, 0.7)'
              : 'text.secondary'
          })}
        >
          {progressText}
        </Typography>
      </Box>

      <StepIndicator>
        {steps.map((step, index) => (
          <NoShadowTooltip key={index} title={step.title}>
            <StepDot
              role="button"
              tabIndex={index < activeStep ? 0 : -1}
              aria-label={`Go to step ${index + 1}: ${step.title}`}
              active={index === activeStep}
              completed={index < activeStep}
              onClick={() => {
                if (index < activeStep) handleStepTransition(index);
              }}
            />
          </NoShadowTooltip>
        ))}
      </StepIndicator>

      <DialogTitle sx={{ pb: 1 }}>
        <Typography
          variant="h5"
          component="div"
          sx={(theme) => ({
            fontWeight: 600,
            color: theme.palette.actualMode === 'wallpaper'
              ? '#FFFFFF'
              : (theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000')
          })}
        >
          {showSummary ? t('agtUps.profileSetup.review.title') : steps[activeStep].title}
        </Typography>
        <Typography
          variant="body1"
          sx={(theme) => ({
            mt: 1,
            color: theme.palette.actualMode === 'wallpaper'
              ? 'rgba(255, 255, 255, 0.85)'
              : 'text.secondary'
          })}
        >
          {showSummary
            ? t('agtUps.profileSetup.review.description')
            : steps[activeStep].description}
        </Typography>
      </DialogTitle>

      <StepContent sx={{ opacity: transitioning ? 0 : 1 }}>
        {showSummary ? (
          <Suspense fallback={<div>Loading summary...</div>}>
            <Fade in timeout={500} mountOnEnter unmountOnExit>
              <div>{summaryContent}</div>
            </Fade>
          </Suspense>
        ) : (
          steps[activeStep].component
        )}
      </StepContent>

      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        {(activeStep > 0 || showSummary) && (
          <Button
            onClick={handleBack}
            startIcon={<ArrowBack sx={(theme) => ({
              color: theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : '#0385FF'
            })} />}
            sx={(theme) => {
              const isWallpaperMode = theme.palette.actualMode === 'wallpaper';
              return {
                textTransform: 'none',
                borderRadius: '28px',
                color: isWallpaperMode ? '#FFFFFF' : '#0385FF',
                '&:hover': {
                  backgroundColor: isWallpaperMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(3, 133, 255, 0.08)'
                }
              };
            }}
          >
            {t('agtUps.profileSetup.navigation.back')}
          </Button>
        )}

        {onSaveDraft && (
          <Button
            onClick={onSaveDraft}
            sx={(theme) => {
              const isWallpaperMode = theme.palette.actualMode === 'wallpaper';
              return {
                textTransform: 'none',
                borderRadius: '28px',
                color: isWallpaperMode ? '#FFFFFF' : undefined,
                '&:hover': {
                  backgroundColor: isWallpaperMode ? 'rgba(255, 255, 255, 0.1)' : undefined
                }
              };
            }}
          >
            {t('agtUps.profileSetup.navigation.saveDraft')}
          </Button>
        )}

        <Box sx={{ flex: 1 }} />

        <Button
          variant="contained"
          disableElevation
          onClick={handleNext}
          disabled={!isStepValid(activeStep) || isSubmitting}
          endIcon={!showSummary && <ArrowForward />}
          sx={(theme) => {
            const isWallpaperMode = theme.palette.actualMode === 'wallpaper';
            return {
              textTransform: 'none',
              borderRadius: '28px',
              backgroundColor: isWallpaperMode ? '#FFFFFF' : '#0385FF',
              color: isWallpaperMode ? '#000000' : '#FFFFFF',
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: isWallpaperMode ? 'rgba(255, 255, 255, 0.85)' : '#0266cc',
                boxShadow: 'none'
              },
              '&.Mui-disabled': {
                backgroundColor: isWallpaperMode ? 'rgba(255, 255, 255, 0.3)' : undefined,
                color: isWallpaperMode ? 'rgba(0, 0, 0, 0.3)' : undefined
              }
            };
          }}
        >
          {isSubmitting
            ? t('agtUps.profileSetup.navigation.submitting')
            : showSummary
              ? t('agtUps.profileSetup.navigation.createProfile')
              : activeStep === steps.length - 1
                ? t('agtUps.profileSetup.navigation.review')
                : t('agtUps.profileSetup.navigation.continue')}
        </Button>
      </DialogActions>
    </StyledDialog>
  );
};

export default ProfileSetupWizard;